import { z } from 'zod';

import type {
  CompanyReceiverDirectoryItem,
  CompanyReceiverDirectoryRecord,
  CompanyReceiverStatus,
  CreateCompanyReceiverRequest,
  CreateCompanyReceiverResponse,
  GlobalRegulatoryEntityDirectoryRecord,
  InternalRcScopeDirectoryRecord,
  InternalRcScopeReceiverDirectoryItem,
  ReceiverDirectoryItem,
  ReceiverDirectorySnapshotResponse,
  RegulatoryEntityDirectoryItem,
  SearchReceiverDirectoryRequest,
  SearchReceiverDirectoryResponse,
  UpdateCompanyReceiverRequest,
  UpdateCompanyReceiverResponse,
} from './receiver-directory.dto.js';
import { companyReceiverStatusValues } from './receiver-directory.dto.js';
import {
  CompanyReceiverCounterpartyRefSchema,
  CompanyReceiverCounterpartySelectionSchema,
  CompanyReceiverTypeSchema,
  CounterpartyRefSchema,
  CounterpartySearchSourceSchema,
  InternalRcScopeCounterpartyRefSchema,
  InternalRcScopeCounterpartySelectionSchema,
  PartyAddressSnapshotSchema,
  RegulatoryEntityCounterpartyRefSchema,
  RegulatoryEntityCounterpartySelectionSchema,
  RegulatoryIdentitySchema,
} from '../counterparties/counterparties.schema.js';
import { RcScopeTypeSchema } from '../shipments/shipments-types.js';

const trimmedStringSchema = z.string().trim().min(1);
const timestampSchema = z.iso.datetime();

export const CompanyReceiverStatusSchema = z.enum(
  companyReceiverStatusValues,
) satisfies z.ZodType<CompanyReceiverStatus>;

export const CompanyReceiverDirectoryRecordSchema = z
  .object({
    id: z.uuid(),
    ownerCompanyId: z.uuid(),
    receiverDirectoryScopeId: z.uuid(),
    displayName: trimmedStringSchema,
    receiverType: CompanyReceiverTypeSchema,
    referenceCode: trimmedStringSchema.nullable().optional(),
    regulatoryIdentity: RegulatoryIdentitySchema,
    regulatoryEntityId: z.uuid().nullable().optional(),
    counterpartyRef: CompanyReceiverCounterpartyRefSchema,
    address: PartyAddressSnapshotSchema.nullable().optional(),
    status: CompanyReceiverStatusSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strict() satisfies z.ZodType<CompanyReceiverDirectoryRecord>;

export const InternalRcScopeDirectoryRecordSchema = z
  .object({
    id: trimmedStringSchema,
    companyId: z.uuid(),
    rcScopeType: RcScopeTypeSchema,
    rcScopeId: z.uuid(),
    displayName: trimmedStringSchema,
    companyName: trimmedStringSchema.nullable(),
    storeName: trimmedStringSchema.nullable(),
    locationName: trimmedStringSchema.nullable(),
    referenceCode: trimmedStringSchema.nullable().optional(),
    regulatoryIdentity: RegulatoryIdentitySchema,
    regulatoryEntityId: z.uuid().nullable().optional(),
    counterpartyRef: InternalRcScopeCounterpartyRefSchema,
    address: PartyAddressSnapshotSchema.nullable().optional(),
    isActive: z.boolean(),
    updatedAt: timestampSchema,
  })
  .strict() satisfies z.ZodType<InternalRcScopeDirectoryRecord>;

export const GlobalRegulatoryEntityDirectoryRecordSchema = z
  .object({
    id: z.uuid(),
    displayName: trimmedStringSchema,
    referenceCode: trimmedStringSchema.nullable().optional(),
    regulatoryIdentity: RegulatoryIdentitySchema,
    receiverType: CompanyReceiverTypeSchema.nullable().optional(),
    counterpartyRef: RegulatoryEntityCounterpartyRefSchema,
    address: PartyAddressSnapshotSchema.nullable().optional(),
    updatedAt: timestampSchema,
  })
  .strict() satisfies z.ZodType<GlobalRegulatoryEntityDirectoryRecord>;

const ReceiverDirectoryItemBaseShape = {
  id: trimmedStringSchema,
  source: CounterpartySearchSourceSchema,
  displayName: trimmedStringSchema,
  companyName: trimmedStringSchema.nullable(),
  referenceCode: trimmedStringSchema.nullable().optional(),
  regulatoryIdentity: RegulatoryIdentitySchema,
  regulatoryEntityId: z.uuid().nullable().optional(),
  address: PartyAddressSnapshotSchema.nullable().optional(),
  isActive: z.boolean(),
  duplicateWarning: trimmedStringSchema.nullable().optional(),
};

export const InternalRcScopeReceiverDirectoryItemSchema = z
  .object({
    ...ReceiverDirectoryItemBaseShape,
    kind: z.literal('INTERNAL_RC_SCOPE'),
    source: z.literal('internal'),
    companyId: z.uuid(),
    rcScopeType: RcScopeTypeSchema,
    rcScopeId: z.uuid(),
    storeName: trimmedStringSchema.nullable(),
    locationName: trimmedStringSchema.nullable(),
    counterpartyRef: InternalRcScopeCounterpartyRefSchema,
    selection: InternalRcScopeCounterpartySelectionSchema,
  })
  .strict() satisfies z.ZodType<InternalRcScopeReceiverDirectoryItem>;

export const CompanyReceiverDirectoryItemSchema = z
  .object({
    ...ReceiverDirectoryItemBaseShape,
    kind: z.literal('COMPANY_RECEIVER'),
    source: z.literal('company'),
    companyReceiverId: z.uuid(),
    receiverType: CompanyReceiverTypeSchema,
    counterpartyRef: CompanyReceiverCounterpartyRefSchema,
    selection: CompanyReceiverCounterpartySelectionSchema,
  })
  .strict() satisfies z.ZodType<CompanyReceiverDirectoryItem>;

export const RegulatoryEntityDirectoryItemSchema = z
  .object({
    ...ReceiverDirectoryItemBaseShape,
    kind: z.literal('REGULATORY_ENTITY'),
    source: z.literal('global'),
    companyName: z.null(),
    regulatoryEntityId: z.uuid(),
    receiverType: CompanyReceiverTypeSchema.nullable().optional(),
    counterpartyRef: RegulatoryEntityCounterpartyRefSchema,
    selection: RegulatoryEntityCounterpartySelectionSchema,
  })
  .strict() satisfies z.ZodType<RegulatoryEntityDirectoryItem>;

export const ReceiverDirectoryItemSchema = z.discriminatedUnion('kind', [
  InternalRcScopeReceiverDirectoryItemSchema,
  CompanyReceiverDirectoryItemSchema,
  RegulatoryEntityDirectoryItemSchema,
]) satisfies z.ZodType<ReceiverDirectoryItem>;

export const SearchReceiverDirectoryRequestSchema = z
  .object({
    companyId: z.uuid(),
    query: z.string().trim().optional(),
    limit: z.number().int().min(1).max(50).optional(),
    excludeCounterpartyRef: CounterpartyRefSchema.optional(),
  })
  .strict() satisfies z.ZodType<SearchReceiverDirectoryRequest>;

export const SearchReceiverDirectoryResponseSchema = z
  .object({
    items: z.array(ReceiverDirectoryItemSchema),
    canQuickCreateCompanyReceiver: z.boolean(),
  })
  .strict() satisfies z.ZodType<SearchReceiverDirectoryResponse>;

export const ReceiverDirectorySnapshotResponseSchema = z
  .object({
    receiverDirectoryScopeId: z.uuid(),
    internalRcScopes: z.array(InternalRcScopeDirectoryRecordSchema),
    companyReceivers: z.array(CompanyReceiverDirectoryRecordSchema),
    globalRegulatoryEntities: z.array(GlobalRegulatoryEntityDirectoryRecordSchema),
  })
  .strict() satisfies z.ZodType<ReceiverDirectorySnapshotResponse>;

export const CreateCompanyReceiverRequestSchema = z
  .object({
    companyId: z.uuid(),
    displayName: trimmedStringSchema,
    receiverType: CompanyReceiverTypeSchema,
    referenceCode: trimmedStringSchema.nullable().optional(),
    regulatoryIdentity: RegulatoryIdentitySchema,
    address: PartyAddressSnapshotSchema.nullable().optional(),
  })
  .strict() satisfies z.ZodType<CreateCompanyReceiverRequest>;

export const CreateCompanyReceiverResponseSchema = z
  .object({
    companyReceiver: CompanyReceiverDirectoryRecordSchema,
    directoryItem: CompanyReceiverDirectoryItemSchema,
  })
  .strict() satisfies z.ZodType<CreateCompanyReceiverResponse>;

export const UpdateCompanyReceiverRequestSchema = z
  .object({
    companyId: z.uuid(),
    companyReceiverId: z.uuid(),
    displayName: trimmedStringSchema.optional(),
    receiverType: CompanyReceiverTypeSchema.optional(),
    referenceCode: trimmedStringSchema.nullable().optional(),
    regulatoryIdentity: RegulatoryIdentitySchema.optional(),
    address: PartyAddressSnapshotSchema.nullable().optional(),
    status: CompanyReceiverStatusSchema.optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.displayName !== undefined
      || value.receiverType !== undefined
      || value.referenceCode !== undefined
      || value.regulatoryIdentity !== undefined
      || value.address !== undefined
      || value.status !== undefined,
    { message: 'At least one field must be provided to update company receiver' },
  ) satisfies z.ZodType<UpdateCompanyReceiverRequest>;

export const UpdateCompanyReceiverResponseSchema = z
  .object({
    companyReceiver: CompanyReceiverDirectoryRecordSchema,
    directoryItem: CompanyReceiverDirectoryItemSchema,
  })
  .strict() satisfies z.ZodType<UpdateCompanyReceiverResponse>;
